import { FirebaseError } from "firebase/app";
import { ApiLoadingState } from "./types";

export const MFA_REQUIRED_ERROR_CODE = "auth/multi-factor-auth-required";

export type MfaChallengeErrorKind = "invalid_code" | "expired" | "rate_limited" | "generic";

/**
 * English fallbacks, one per kind. Surfaces translate `kind` through their own i18n and must
 * not render these; they keep `Error.message` readable in logs and Sentry.
 */
export const MFA_CHALLENGE_FALLBACK_MESSAGES: Record<MfaChallengeErrorKind, string> = {
  // Firebase returns one code for a reused, expired and mistyped code alike, so this has to
  // cover all three without guessing which happened.
  invalid_code:
    "That code didn't work. Codes can only be used once and change every 30 seconds — wait for the next one and try again.",
  expired: "Your sign-in session expired. Please sign in again.",
  rate_limited: "Too many attempts. Wait a minute, then try again.",
  generic: "Could not verify the code. Please try again.",
};

export class MfaChallengeError extends Error {
  readonly kind: MfaChallengeErrorKind;

  constructor(kind: MfaChallengeErrorKind, message: string = MFA_CHALLENGE_FALLBACK_MESSAGES[kind]) {
    super(message);
    this.name = "MfaChallengeError";
    this.kind = kind;
  }
}

export type MfaChallengeStateSlice = {
  mfaChallengePending: boolean;
  loading: ApiLoadingState;
  isProcessingDeepLink: boolean;
  authError: string | null;
  authErrorKind: MfaChallengeErrorKind | null;
  deepLink: string | null;
};

export type RedirectFailureClassification = "mfa" | "fatal" | "ignore";

export function isMultiFactorAuthRequiredError(error: unknown): boolean {
  return error instanceof FirebaseError && error.code === MFA_REQUIRED_ERROR_CODE;
}

export function pendingChallengeAuthPatch(): Pick<
  MfaChallengeStateSlice,
  "mfaChallengePending" | "loading" | "isProcessingDeepLink" | "authError" | "authErrorKind"
> {
  return {
    mfaChallengePending: true,
    loading: ApiLoadingState.notLoading,
    isProcessingDeepLink: false,
    authError: null,
    authErrorKind: null,
  };
}

export function cancelChallengePatch(): Pick<MfaChallengeStateSlice, "mfaChallengePending"> {
  return { mfaChallengePending: false };
}

/** After TOTP succeeds, keep a spinner until onAuthStateChanged / refreshSession supplies a user. */
export function resolvedChallengeAuthPatch(): Pick<
  MfaChallengeStateSlice,
  "mfaChallengePending" | "loading" | "isProcessingDeepLink"
> {
  return {
    mfaChallengePending: false,
    loading: ApiLoadingState.loading,
    isProcessingDeepLink: false,
  };
}

/**
 * Drop the TOTP step and put the failure on the login form. Carries the `kind` so the login
 * page can translate it; `authError` keeps an English fallback for logs and for surfaces
 * that have not adopted the kind yet.
 */
export function expiredChallengeAuthPatch(
  kind: MfaChallengeErrorKind,
): Pick<
  MfaChallengeStateSlice,
  "mfaChallengePending" | "loading" | "isProcessingDeepLink" | "authError" | "authErrorKind"
> {
  return {
    mfaChallengePending: false,
    loading: ApiLoadingState.notLoading,
    isProcessingDeepLink: false,
    authError: MFA_CHALLENGE_FALLBACK_MESSAGES[kind],
    authErrorKind: kind,
  };
}

export function classifyRedirectFailure({
  error,
  mfaChallengePending,
  resolverCaptured = false,
}: {
  error: unknown;
  mfaChallengePending: boolean;
  resolverCaptured?: boolean;
}): RedirectFailureClassification {
  const pending = mfaChallengePending || resolverCaptured;
  if (error == null) {
    return pending ? "ignore" : "fatal";
  }
  if (isMultiFactorAuthRequiredError(error)) {
    return "mfa";
  }
  if (pending) {
    return "ignore";
  }
  return "fatal";
}

export type InterpretSignInErrorResult =
  | { action: "mfa_pending" }
  | { action: "rethrow" }
  | { action: "fatal"; message: string };

export function interpretSignInError(error: unknown): InterpretSignInErrorResult {
  if (isMultiFactorAuthRequiredError(error)) {
    return { action: "mfa_pending" };
  }
  return { action: "rethrow" };
}

export function formatRedirectFatalError(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    const firebaseError = error as { code?: string; message?: string };
    return `${firebaseError.code ?? ""} ${firebaseError.message ?? ""}`.trim() || "Sign-in failed";
  }
  if (error instanceof Error) {
    return error.message || "Sign-in failed";
  }
  return "Sign-in failed";
}

export function applySignedOutWhileChallengePending<T extends MfaChallengeStateSlice & Record<string, unknown>>(
  state: T,
  sessionEndedForSignedInUser: boolean,
): T {
  return {
    ...state,
    user: null,
    isEmailVerified: false,
    loading: ApiLoadingState.notLoading,
    permissions: null,
    isProcessingDeepLink: false,
    deepLink: sessionEndedForSignedInUser ? null : state.deepLink,
  };
}

export function shouldClearDeepLinkOnSignedOut({
  sessionEndedForSignedInUser,
  mfaChallengePending,
  resolverCaptured = false,
  mfaRetryPending = false,
}: {
  sessionEndedForSignedInUser: boolean;
  mfaChallengePending: boolean;
  resolverCaptured?: boolean;
  mfaRetryPending?: boolean;
}): boolean {
  return sessionEndedForSignedInUser && !mfaChallengePending && !resolverCaptured && !mfaRetryPending;
}

export type AuthStateTransition = "authenticated" | "signed_out_preserve_deep_link" | "signed_out_clear_deep_link";

export function classifyAuthStateTransition({
  hasUser,
  sessionEndedForSignedInUser,
  mfaChallengePending,
  resolverCaptured = false,
  mfaRetryPending = false,
}: {
  hasUser: boolean;
  sessionEndedForSignedInUser: boolean;
  mfaChallengePending: boolean;
  resolverCaptured?: boolean;
  mfaRetryPending?: boolean;
}): AuthStateTransition {
  if (hasUser) return "authenticated";
  return shouldClearDeepLinkOnSignedOut({
    sessionEndedForSignedInUser,
    mfaChallengePending,
    resolverCaptured,
    mfaRetryPending,
  })
    ? "signed_out_clear_deep_link"
    : "signed_out_preserve_deep_link";
}

export function mapMfaResolveError(error: unknown): MfaChallengeError {
  if (error instanceof MfaChallengeError) {
    return error;
  }
  if (error instanceof FirebaseError) {
    // `code-expired` has the same remedy as a reused or mistyped code: wait for the next one.
    if (error.code === "auth/invalid-verification-code" || error.code === "auth/code-expired") {
      return new MfaChallengeError("invalid_code");
    }
    if (error.code === "auth/missing-multi-factor-session" || error.code === "auth/invalid-multi-factor-session") {
      return new MfaChallengeError("expired");
    }
    // Without this, a rate-limited user is told to "try again" indefinitely.
    if (error.code === "auth/too-many-requests") {
      return new MfaChallengeError("rate_limited");
    }
  }
  return new MfaChallengeError("generic");
}
